<template>
  <div>
    <!-- Loading spinner -->
    <div v-if="isLoading" class="py-8 flex items-center justify-center">
      <Spinner size="md" color-scheme="primary" />
    </div>

    <!-- Empty state -->
    <div v-else-if="!items.length" class="text-center text-gray-500 py-16">
      <p>{{ mode === 'library' ? $t('IMAGE_LIBRARY.EMPTY.FOLDERS') : $t('IMAGE_LIBRARY.EMPTY.ITEMS') }}</p>
    </div>

    <!-- Grid 12 card/trang -->
    <!-- Folder Grid (fewer columns) -->
    <div v-if="!isLoading && mode === 'library'" class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-2">
      <div v-for="item in items" :key="item.id"
        class="border border-n-gray-1 dark:border-n-vars-black-bd rounded-md p-4 transition-all cursor-pointer shadow-sm hover:shadow-md min-h-[80px] flex flex-col"
        @click="$emit('card-click', item)">
        <div class="flex items-end justify-between relative">
          <div class="flex items-center gap-3 flex-1 min-w-0">
            <div>
              <svg viewBox="0 0 48 48" version="1" xmlns="http://www.w3.org/2000/svg" enable-background="new 0 0 48 48"
                fill="#000000" class="w-10 h-10 text-white">
                <g id="SVGRepo_iconCarrier">
                  <path fill="#FFA000" d="M40,12H22l-4-4H8c-2.2,0-4,1.8-4,4v8h40v-4C44,13.8,42.2,12,40,12z"></path>
                  <path fill="#FFCA28"
                    d="M40,12H8c-2.2,0-4,1.8-4,4v20c0,2.2,1.8,4,4,4h32c2.2,0,4-1.8,4-4V16C44,13.8,42.2,12,40,12z">
                  </path>
                </g>
              </svg>
            </div>

            <div class="flex-1 min-w-0">
              <div class="font-semibold text-gray-900 truncate text-sm" :title="item.name">
                {{ item.name }}
              </div>
              <div class="text-xs text-gray-500 mt-1 flex items-center gap-1">
                <span>{{ item.image_items_count || 0 }}</span>
                <span>{{ $t('IMAGE_LIBRARY.IMAGES') }}</span>
              </div>
            </div>

            <woot-dropdown-menu @click.stop class="flex gap-1">
              <woot-dropdown-item
                v-tooltip.left="mode === 'library' ? $t('IMAGE_LIBRARY.TOOLTIPS.EDIT_FOLDER') : $t('IMAGE_LIBRARY.TOOLTIPS.EDIT')"
                @click="$emit('action', { type: 'edit', item })"
                class="border border-n-gray-1 dark:!bg-n-vars-black-bd/80 dark:!border-[#252B3799] rounded p-1"
              >
                <span class="block">
                  <fluent-icon icon="edit" size="14" />
                </span>
              </woot-dropdown-item>
              <woot-dropdown-item
                variant="danger"
                v-tooltip.left="mode === 'library' ? $t('IMAGE_LIBRARY.TOOLTIPS.DELETE_FOLDER') : $t('IMAGE_LIBRARY.TOOLTIPS.DELETE')"
                @click="$emit('action', { type: 'delete', item })"
                class="border border-n-gray-1 dark:!bg-n-vars-black-bd/80 dark:!border-[#252B3799] rounded p-1"
              >
                <span class="block">
                  <fluent-icon icon="delete" size="14" />
                </span>
              </woot-dropdown-item>
            </woot-dropdown-menu>
          </div>

          <!-- ✅ Badge hiển thị ở góc dưới phải -->
          <div v-if="item.business_category" class="absolute -bottom-4 -right-2">
            <span class="inline-flex items-center px-1 rounded-full text-[10px] font-medium" :style="{
              backgroundColor: item.business_category.color + '15',
              color: item.business_category.color,
              border: `1px solid ${item.business_category.color}30`
            }">
              <span class="w-1 h-1 rounded-full mr-1" :style="{ backgroundColor: item.business_category.color }"></span>
              {{ item.business_category.name }}
            </span>
          </div>

          <div v-else-if="item.category" class="absolute bottom-0 right-2">
            <span
              class="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
              {{ item.category }}
            </span>
          </div>
        </div>
      </div>
    </div>

    <!-- Image Grid (more columns) -->
    <!-- Image Grid (more columns) -->
    <div v-else-if="!isLoading" class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-6 gap-4">
      <div v-for="item in items" :key="item.id"
        class="group border border-n-gray-1 dark:border-n-vars-black-bd rounded-md transition-all duration-200 cursor-pointer shadow-sm hover:shadow-lg hover:-translate-y-0.5 flex flex-col relative bg-white dark:bg-n-vars-black-bd"
        :class="[enableSelection && isSelected(item.id) ? 'ring-0.5 ring-grass-500 border-grass-500 dark:ring-grass-400 dark:border-grass-400' : '', !item.is_active ? 'opacity-60' : '']"
        @click="$emit('card-click', item)">
        
        <!-- Image Container with Overlay -->
        <div class="relative aspect-square w-full overflow-hidden rounded-md bg-gray-50 dark:bg-gray-800">
          <img :src="item.thumb_url || item.file_url || placeholder" 
               class="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" />
          
          <!-- Overlay on Hover -->
          <div class="absolute inset-0 bg-gradient-to-t from-black/60 via-black/0 to-black/0 opacity-0 group-hover:opacity-100 transition-opacity duration-200"></div>
          
          <!-- Checkbox with backdrop -->
          <div v-if="enableSelection" class="absolute top-3 left-3 z-10">
              <input
                type="checkbox"
                class="h-4 w-4 cursor-pointer rounded border-slate-300 text-hichat-500 focus:ring-hichat-400 focus:ring-offset-0 !mt-0 leading-none outline-none"
                :checked="isSelected(item.id)"
                @click.stop
                @change="$emit('toggle-select', { id: item.id, checked: $event.target.checked })"
              />
          </div>
          
          <!-- Hidden state handled by card opacity; badge removed -->
          
          <!-- Action Buttons (Show on Hover) -->
          <div class="absolute bottom-0 left-0 right-0 p-3 translate-y-full group-hover:translate-y-0 transition-transform duration-200" @click.stop>
            <div class="flex items-center justify-center gap-1.5 bg-white/70 dark:bg-n-vars-black-bd/80 backdrop-blur-sm rounded-lg p-1.5 shadow-xl">
              <woot-button size="small" variant="clear"
                v-tooltip.top="$t('IMAGE_LIBRARY.TOOLTIPS.EDIT')"
                :title="$t('IMAGE_LIBRARY.TOOLTIPS.EDIT')"
                class="!p-2 hover:bg-gray-100 dark:hover:!bg-white/10 rounded-md transition-colors !text-n-gray-7 dark:!text-n-gray-3"
                @click="$emit('action', { type: 'edit', item })">
                <fluent-icon icon="edit" size="14" />
              </woot-button>
              <woot-button size="small" variant="clear"
                v-tooltip.top="item.is_active ? $t('IMAGE_LIBRARY.TOOLTIPS.HIDE') : $t('IMAGE_LIBRARY.TOOLTIPS.SHOW')"
                class="!p-2 hover:bg-gray-100 dark:hover:!bg-white/10 rounded-md transition-colors !text-n-gray-7 dark:!text-n-gray-3"
                :title="item.is_active ? $t('IMAGE_LIBRARY.TOOLTIPS.HIDE') : $t('IMAGE_LIBRARY.TOOLTIPS.SHOW')"
                @click="$emit('action', { type: 'toggle', item })">
                <fluent-icon :icon="item.is_active ? 'eye-hide' : 'eye-show'" size="14" />
              </woot-button>
              <woot-button size="small" variant="clear"
                v-tooltip.top="$t('IMAGE_LIBRARY.TOOLTIPS.COPY_URL')"
                :title="$t('IMAGE_LIBRARY.TOOLTIPS.COPY_URL')"
                class="!p-2 hover:bg-gray-100 dark:hover:!bg-white/10 rounded-md transition-colors !text-n-gray-7 dark:!text-n-gray-3"
                @click="$emit('action', { type: 'copy_url', item })">
                <fluent-icon icon="link" size="14" />
              </woot-button>
              <woot-button size="small" variant="clear"
                v-tooltip.top="$t('IMAGE_LIBRARY.TOOLTIPS.DELETE')"
                :title="$t('IMAGE_LIBRARY.TOOLTIPS.DELETE')"
                class="!p-2 hover:bg-red-50 dark:hover:!bg-red-900/20 rounded-md transition-colors !text-red-600 dark:!text-red-400"
                @click="$emit('action', { type: 'delete', item })">
                <fluent-icon icon="delete" size="14" />
              </woot-button>
            </div>
          </div>
        </div>
        
        <!-- Card Content -->
        <div class="p-3 flex flex-col flex-1 min-h-0">
          <h3 class="font-semibold text-sm text-gray-900 dark:text-gray-100 truncate mb-1" 
              :title="item.title || item.filename">
            {{ item.title || item.filename }}
          </h3>

          <p v-if="item.id" class="text-[11px] text-gray-400 dark:text-gray-500 mb-1">
            ID: {{ item.id }}
          </p>
          
          <p v-if="item.description" 
             class="text-xs text-gray-600 dark:text-gray-400 line-clamp-2 mb-2 flex-1">
            {{ item.description }}
          </p>
          
          <div class="flex items-center justify-between mt-auto pt-2 border-t border-gray-100 dark:border-gray-700">
            <span class="text-xs text-gray-500 dark:text-gray-400 font-medium">
              {{ humanSize(item.file_size) }}
            </span>
            <span v-if="item.created_at" class="text-xs text-gray-500 dark:text-gray-400">
              {{ formatDate(item.created_at) }}
            </span>
          </div>
        </div>
      </div>
    </div>

    <!-- Pagination -->
    <div v-if="!isLoading && total > pageSize" class="mt-6 flex justify-end">
      <woot-pagination :current-page="page" :total-items="total" :per-page="pageSize"
        @page-change="$emit('page-change', $event)" />
    </div>
  </div>
</template>

<script>
import Spinner from 'shared/components/Spinner.vue'
import placeholder from 'dashboard/assets/images/no_page_image.png'
import { formatDateTimeLocalized } from 'shared/helpers/DateHelper'

export default {
  name: 'ImageGrid',
  components: { Spinner },
  props: {
    mode: { type: String, required: true },
    items: { type: Array, default: () => [] },
    isLoading: { type: Boolean, default: false },
    page: { type: Number, default: 1 },
    total: { type: Number, default: 0 },
    pageSize: { type: Number, default: 12 },
    enableSelection: { type: Boolean, default: false },
    selectedIds: { type: Array, default: () => [] },
  },
  emits: ['card-click', 'action', 'page-change', 'toggle-select'],
  data() {
    return { placeholder }
  },
  methods: {
    isSelected(id) {
      return this.selectedIds.includes(id)
    },
    humanSize(bytes) {
      if (!bytes) return ''
      const i = Math.floor(Math.log(bytes) / Math.log(1024))
      const sizes = ['B', 'KB', 'MB', 'GB']
      return `${(bytes / (1024 ** i)).toFixed(2)} ${sizes[i]}`
    },
    formatDate(value) {
      if (!value) return ''
      return formatDateTimeLocalized(value, {
        includeSeconds: false,
        localeOverride: this.$i18n?.locale,
      })
    },
  },
}
</script>
